import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { styled } from '@uhg-abyss/web/tools/styled';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { DateInput } from '@uhg-abyss/web/ui/DateInput';
import { useFormState } from '@src/state/form/hooks';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { subYears, endOfDay, startOfDay } from 'date-fns';
import { useTIN } from '@src/state/taxInformation/hooks';
import { updateClaimInfo } from '@src/state/form/actions';
import { usePAARegistration } from '@src/state/PAARegistration/hooks';
import { useVerifyClaim } from '@src/state/verifyClaim';
import { useMedicaid } from '@src/state/medicaid';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { config } from '@uhg-abyss/web/tools/config';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { Popover } from '@uhg-abyss/web/ui/Popover';
import { useUser } from '@src/state/user';
import { useOnloading } from '@src/state/landing';
import { Button } from '@uhg-abyss/web/ui/Button';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { Modal } from '@uhg-abyss/web/ui/Modal';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { useDental } from '@src/state/dental/hooks';
import { LagoonContext } from '@src/AppContext/LagoonContext';
import { adobeInteraction, getOrgType } from '@src/common/helpers';
import { setPageHandler } from '@src/common/tools/analytics/helpers';
import { MedicaidId } from './MedicaidId';
import { PaymentInfoHelpModal } from '../PaymentInfoHelpModal';
import { ValidationInfoModal } from '../ValidationInfoModal';

const MIN_BIRTH_DATE = subYears(new Date(), 120);
const MAX_BIRTH_DATE = endOfDay(new Date());

const MainContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
  marginLeft: '-300px',
  width: '1270px',
  zIndex: 1,
});

const SecurityContainer = styled('div', {
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  padding: '25px',
  marginBottom: '20px',
  marginLeft: '-80px',
  width: '800px',
  zIndex: 1,
});

const BoxHeaderText = styled('h4', {
  position: 'relative',
  marginBottom: '20px',
  fontWeight: 400,
  fontSize: '24px',
  width: '100%',
  color: '$primary1',
});

const HorizontalView = styled('div', {
  width: '100%',
  display: 'flex',
  margin: 'auto',
  marginTop: 10,
});

const PaymentInfoHorizontalView = styled(HorizontalView, {
  marginTop: '20px',
  border: 'none',
});

const USMailValidationView = styled('div', {
  marginTop: '20px',
});

const HelpLink = styled('div', {
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  marginLeft: '35%',
  color: '$info1',
});

const LayoutInstruction = styled('p', {
  fontWeight: 'normal',
  fontFamily: '$primary',
  marginBottom: '10px',
});

const HelpText = styled('p', {
  fontWeight: 'normal',
  fontSize: '14px',
  marginBottom: 'auto',
});

const LayoutNeedText = styled('p', {
  fontWeight: 'bold',
  display: 'inline-flex',
  marginBottom: 'revert',
});

const LayoutStyle = styled('div', {
  margin: '10px',
  borderRadius: '3px',
  border: '1px solid rgb(219, 219, 219)',
  background: '$white',
  flex: 1,
  paddingLeft: 20,
  paddingRight: 10,
  paddingTop: 10,
  display: 'flex',
  flexDirection: 'column',
});

const InLine = styled('div', {
  display: 'inline-flex',
  paddingBottom: '10px',
});

const LayoutHeader = styled('h3', {
  fontWeight: 300,
  fontSize: '16px',
  width: '100%',
  color: '$primary1',
  fontFamily: '$heading',
  marginBottom: '20px',
});

const LayoutModal = styled('div', {
  marginBottom: '10px',
  marginTop: '20px',
});

const LayoutAllign = styled('div', {
  paddingRight: 30,
  paddingLeft: 8,
  marginBottom: 'auto',
});

const parseDollarToNumber = (dollarAmount) => {
  return dollarAmount.split(',').join('');
};

export const VerifyClaimV3 = ({
  handleBack,
  handleNext,
  handleError,
  handleMedicaidError,
  showMedicaidError,
  handleStopAll,
  handleDisableCancel,
  address,
}) => {
  const form = useForm();
  const { actions, state } = useFormState({ updateClaimInfo });
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const errorMessages = lagoonContext.getTable('error-messaging');
  const router = useRouter();
  const { token } = useParams();
  const [showSecurityInput, setShowSecurityInput] = useState('');
  const [isProcedureCode, setIsProcedureCode] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [memIdErr, setMemIdErr] = useState(false);
  const [patientDOBError, setPatientDOBError] = useState(false);

  const [claimError, setClaimError] = useState(false);

  const [fDOSError, setFDOSError] = useState(false);

  const [lDOSError, setLDOSError] = useState(false);

  const [procedureCodeErr, setProcedureCodeErr] = useState(false);

  const [revenueCodeErr, setRevenueCodeErr] = useState(false);

  const [paymentRefNumberError, setPaymentRefNumberError] = useState('');

  const [totalAmPaidError, setTotalAmPaidError] = useState('');

  const [paymentInfoNotMatchedError, setPaymentInfoNotMatchedError] =
    useState('');

  const [nonFormSpecificError, setNonFormSpecificError] = useState('');

  const [errMessage, setErrMessage] = useState('');

  const [addressApiError, setAddressApiError] = useState('');
  const [generateKeyError, setGenerateKeyError] = useState('');
  const [infoModelOpen, setInfoModelOpen] = useState(false);
  const [modalErrorCode, setModalErrorCode] = useState('');
  const { TINNumber, TINInfo } = useTIN.state();

  const verifyClaimState = useVerifyClaim.state();
  const { isDental } = useDental.state();

  const {
    getClaim,
    verifyPaymentInfo,
    setMainRadioGroup,
    setUSMailSecurityRadioGroup,
    setReduxIsProcedureCode,
    resetState,
    validateSecurityKey,
    tinAddressApi,
    generateSecurityKey,
    setValidationErrorType,
  } = useVerifyClaim.actions();

  const { verifyMedicaid } = useMedicaid.actions();
  const { resetState: resetStateOfPAA } = usePAARegistration.actions();

  const {
    reduxIsProcedureCode,
    reduxMainRadio,
    reduxUsMailSecurityRadio,
    reduxValidationErrorType,
  } = verifyClaimState;

  const [radioGroup, setRadioGroup] = useState('');

  useEffect(() => {
    if (reduxUsMailSecurityRadio) {
      setShowSecurityInput(reduxUsMailSecurityRadio);
    }
  }, [reduxUsMailSecurityRadio]);

  useEffect(() => {
    if (reduxIsProcedureCode) {
      setIsProcedureCode(reduxIsProcedureCode);
    }
    form.setValue(
      'PAASelfAttestation.verifyClaimMemberId',
      state.registration?.claimInfo?.memberId
    );
    form.setValue(
      'PAASelfAttestation.verifyClaimPatientDOB',
      state.registration?.claimInfo?.patientDob
    );
    form.setValue(
      'PAASelfAttestation.verifyClaimPatienClaim',
      state.registration?.claimInfo?.claimNumber
    );
    form.setValue(
      'PAASelfAttestation.fDOS',
      state.registration?.claimInfo?.fDOS
    );
    form.setValue(
      'PAASelfAttestation.lDOS',
      state.registration?.claimInfo?.lDOS
    );
    form.setValue(
      'PAASelfAttestation.verifyClaimprocedureCode',
      state.registration?.claimInfo?.procedureCode
    );
    form.setValue(
      'PAASelfAttestation.verifyClaimrevenueCode',
      state.registration?.claimInfo?.revenueCode
    );
  }, [reduxIsProcedureCode]);

  const getOptions = () => {
    const options = (address || []).map((element) => {
      return {
        label: element.value,
        value: element.addressId,
      };
    });
    return options;
  };

  const { userUUID, isAPIMarketPlaceUser } = useUser.state();
  const { currentFlow } = useOnloading.state();

  const clearClaimInformation = () => {
    actions.updateClaimInfo({
      memberId: '',
      patientDob: '',
      claimNumber: '',
      fDOS: '',
      lDOS: '',
      procedureCode: '',
      revenueCode: '',
    });
  };

  useEffect(() => {
    if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
      const orgType = getOrgType(currentFlow);
      const section1 = 'paa registration';
      const section2 = 'company verification';
      const pageName = `digitalsecurity:${section1}:${section2}`;
      setPageHandler('company verification');
      adobeTaggingEvent('ON_PAGE_LOAD', {
        pageName,
        orgType,
        organization: TINInfo?.corpName,
        uuid: userUUID,
        tin: TINNumber,
        section1,
        section2,
        section3: '',
        siteSectionL3: 'paa registration',
      });
    }
  }, []);

  const errorActions = ({ error }, type = null) => {
    const { code, description } = error;
    if (code === 'DSR:V:CLV:MAX') {
      setValidationErrorType('CLAIM');
      return;
    }
    if (code === 'DSR:V:PAV:MAX') {
      setValidationErrorType('PAYMENT');
      return;
    }
    if (code === 'DSR:V:UMV:MAX') {
      setValidationErrorType('MAIL');
      return;
    }
    if (code === 'DSR:V:PVL:MAX') {
      resetState();
      resetStateOfPAA();
      form.reset('PAASelfAttestation');
      clearClaimInformation();
      if (type === 'UsMailValidation') {
        router.navigate(`/PAAlockMessage-page/verifyKey/${token}`);
      } else if (type === 'claimValidation') {
        router.navigate(`/PAAlockMessage-page/verifyClaim/${token}`);
      } else if (type === 'medicaidValidation') {
        router.navigate(`/PAAlockMessage-page/verifyMedicaid/${token}`);
      } else {
        router.navigate(`/PAAlockMessage-page/paymentInfo/${token}`);
      }
      return;
    }

    if (code === 'DS:V:TNVP:PRNM' || code === 'DS:V:TNVP:PRNF') {
      setPaymentRefNumberError(description);
      setTotalAmPaidError('');
      setPaymentInfoNotMatchedError('');
      setNonFormSpecificError('');
      setAddressApiError('');
      setGenerateKeyError('');
    } else if (code === 'DS:V:TNVP:APDM' || code === 'DS:V:TNVP:AMPM') {
      setPaymentRefNumberError('');
      setTotalAmPaidError(description);
      setPaymentInfoNotMatchedError('');
      setNonFormSpecificError('');
      setAddressApiError('');
      setGenerateKeyError('');
    } else if (code === 'DS:V:TNVP:PINM') {
      setPaymentRefNumberError('');
      setTotalAmPaidError('');
      setPaymentInfoNotMatchedError(description);
      setNonFormSpecificError('');
      setAddressApiError('');
      setGenerateKeyError('');
    } else if (code === 'DSR:E:UMGA:TER' || code === 'DSR:E:UMGK:TER') {
      handleError(true);
      setPaymentRefNumberError('');
      setTotalAmPaidError('');
      setPaymentInfoNotMatchedError('');
      setNonFormSpecificError('');
      setAddressApiError(description);
      setGenerateKeyError('');
    } else if (code === 'DSR:V:UMGA:ND' || code === 'DSR:V:UMGK:ND') {
      handleError(true);
      setPaymentRefNumberError('');
      setTotalAmPaidError('');
      setPaymentInfoNotMatchedError('');
      setNonFormSpecificError('');
      setAddressApiError('');
      setGenerateKeyError(description);
    } else {
      handleError(true);
      setPaymentRefNumberError('');
      setTotalAmPaidError('');
      setPaymentInfoNotMatchedError('');
      setNonFormSpecificError(
        (code && errorMessages.getRow(code)?.message) ||
          errorMessages.getRow('DEFAULT')?.message
      );
      setAddressApiError('');
      setGenerateKeyError('');
    }
  };

  const operateClaimMemberInfo = () => {
    form.validate(
      'PAASelfAttestation',
      (responseA) => {
        const memberID = responseA?.verifyClaimMemberId;
        const dob = responseA?.verifyClaimPatientDOB;
        const claimNumber = state.registration?.claimInfo?.claimNumber;
        const fDOSData = responseA?.fDOS;
        const lDOSData = responseA?.lDOS;
        const procedureCd =
          (isProcedureCode === 'procedureCode' &&
            state.registration?.claimInfo?.procedureCode) ||
          '';
        const revenueCd =
          (isProcedureCode === 'revenueCode' &&
            state.registration?.claimInfo?.revenueCode) ||
          '';
        const { verifyClaimPatientAcc, verifyClaimPatienPolicy } = responseA;
        const payload = {
          tin: TINNumber,
          requestId: TINInfo.requestId,
          memberId: memberID,
          pan: verifyClaimPatientAcc,
          dob,
          claimNumber,
          procedureCd,
          revenueCd,
          fdos: fDOSData,
          ldos: lDOSData,
          policyNumber: verifyClaimPatienPolicy,
          currentFlow,
          url:
            uiMessages.getRow('REGISTER_API')?.message ||
            config('REGISTER_API'),
          isDental,
        };
        adobeTaggingEvent('VERIFY_CLAIM', {
          verificationType: 'claim and member information',
          firstDateOfService: fDOSData,
          lastDateOfService: lDOSData,
        });
        // Note: This method doesn't seem to do anything.
        // We will NEVER get most of these descriptions back since they are UI, not API
        // Will always go into the else at the bottom which just displays a general banner.
        // So claim errors now will use errorActions instead of claimErrorActions
        //
        // const claimErrorActions = ({ error }) => {
        //   const { description, code } = error;
        //   const setInputError = (id, setError) => {
        //     setError(true);
        //     setErrMessage(
        //       (code && errorMessages.getRow(code)?.message) ||
        //         errorMessages.getRow('DEFAULT')?.message
        //     );
        //   };
        //   const errorObj = {
        //     'Member ID Invalid / Mismatch': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimMemberId',
        //         setMemIdErr
        //       );
        //     },

        //     'Claim Not Matched': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimPatienClaim',
        //         setClaimError
        //       );
        //     },

        //     'lastSrvcDt Invalid / Mismatch': () => {
        //       return setInputError('PAASelfAttestation.lDOS', setLDOSError);
        //     },

        //     'lastSrvcDt must be greater than the firstSrvcDt': () => {
        //       return setInputError('PAASelfAttestation.lDOS', setLDOSError);
        //     },

        //     'firstSrvcDt Invalid / Mismatch': () => {
        //       return setInputError('PAASelfAttestation.fDOS', setFDOSError);
        //     },

        //     'DOS Not Matched': () => {
        //       return setInputError('PAASelfAttestation.lDOS', setLDOSError);
        //     },

        //     'Revenue code Not Matched': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimrevenueCode',
        //         setRevenueCodeErr
        //       );
        //     },
        //     'Procedure code Not Matched': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimprocedureCode',
        //         setProcedureCodeErr
        //       );
        //     },

        //     'DOB Not Matched': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimPatientDOB',
        //         setPatientDOBError
        //       );
        //     },
        //     'Member Not found': () => {
        //       return setInputError(
        //         'PAASelfAttestation.verifyClaimMemberId',
        //         setMemIdErr
        //       );
        //     },
        //     'Maximum validate count reached.': () => {
        //       clearClaimInformation();
        //       resetState();
        //       resetStateOfPAA();
        //       form.reset('PAASelfAttestation');
        //       router.navigate(`/PAAlockMessage-page/verifyClaim/${token}`);
        //     },
        //     'DSR:V:PVL:MAX': () => {
        //       clearClaimInformation();
        //       resetState();
        //       resetStateOfPAA();
        //       form.reset('PAASelfAttestation');
        //       router.navigate(`/PAAlockMessage-page/verifyClaim/${token}`);
        //     },
        //     'Maximum validate count reached for Claims.': () => {
        //       setValidationErrorType('CLAIM');
        //     },
        //     'DSR:V:CLV:MAX': () => {
        //       setValidationErrorType('CLAIM');
        //     },
        //     'Maximum validate count reached for Payment.': () => {
        //       setValidationErrorType('PAYMENT');
        //     },
        //     'Maximum validate count reached for US Mail.': () => {
        //       setValidationErrorType('MAIL');
        //     },
        //   };
        //   if (errorObj[description]) {
        //     errorObj[description]();
        //   } else {
        //     handleError(true);
        //   }
        // };

        getClaim(
          payload,
          (claimResponse) => {
            if (claimResponse.error) {
              if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
                adobeTaggingEvent('API_TRANSACTION_ERROR', {
                  errorMessage: claimResponse.error.description,
                  errorApiCode: claimResponse.error.code,
                  errorHttpsCode: claimResponse.error.status,
                });
              }
              form.setValue('PAASelfAttestation', {
                fDOS: '',
                lDOS: '',
                verifyClaimMemberId: '',
                verifyClaimPatienClaim: '',
                verifyClaimPatientDOB: '',
                verifyClaimprocedureCode: '',
                verifyClaimrevenueCode: '',
              });
              errorActions(claimResponse, 'claimValidation');
              return;
            }
            handleNext('claim and member information', payload);
          },
          (err) => {
            form.setValue('PAASelfAttestation', {
              fDOS: '',
              lDOS: '',
              verifyClaimMemberId: '',
              verifyClaimPatienClaim: '',
              verifyClaimPatientDOB: '',
              verifyClaimprocedureCode: '',
              verifyClaimrevenueCode: '',
            });
            errorActions(err, 'claimValidation');
          }
        );
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = [];
          if (error.claimMember === 'procedureCode') {
            if (error.verifyClaimprocedureCode === '') {
              fields.push('procedure code');
            }
          } else if (error.claimMember === 'revenueCode') {
            if (error.verifyClaimrevenueCode === '') {
              fields.push('revenue code');
            }
          }
          if (error.verifyClaimMemberId === '') {
            fields.push('member ID');
          }
          if (error.verifyClaimFirstName === '') {
            fields.push('patient first name');
          }
          if (error.verifyClaimPatientDOB === '') {
            fields.push('patient date of birth');
          }
          if (error.verifyClaimPatienClaim === '') {
            fields.push('claim number');
          }
          if (error.fDOS === '') {
            fields.push('first date of service');
          }
          if (error.lDOS === '') {
            fields.push('last date of service');
          }
          if (fields.length > 0) {
            adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
          }
        }
      }
    );
  };

  useEffect(() => {
    if (reduxValidationErrorType.trim() !== '') {
      setModalErrorCode(reduxValidationErrorType);
      setInfoModelOpen(true);
    }
  }, [reduxValidationErrorType]);

  const operateMedicaidInfo = () => {
    setNonFormSpecificError(false);
    setErrMessage('');
    form.validate('PAASelfAttestation.medicaidIdInput');
    form.validate('PAASelfAttestation.zipCodeInput');

    form.validate('PAASelfAttestation', (formModel, fields) => {
      const payload = {
        requestId: TINInfo.requestId,
        medicaidId: formModel.medicaidIdInput,
        zip: formModel.zipCodeInput,
        url:
          uiMessages.getRow('REGISTER_API')?.message || config('REGISTER_API'),
      };

      verifyMedicaid(payload, (medicaidResponse) => {
        if (!medicaidResponse.medicaidMatch || medicaidResponse.error) {
          form.setValue('PAASelfAttestation', {
            medicaidIdInput: '',
            zipCodeInput: '',
          });
          if (
            medicaidResponse.error &&
            featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active
          ) {
            adobeTaggingEvent('API_TRANSACTION_ERROR', {
              errorMessage: medicaidResponse.error.description,
              errorApiCode: medicaidResponse.error.code,
              errorHttpsCode: medicaidResponse.error.status,
            });
          }

          if (medicaidResponse?.error?.code === 'DSR:V:PVL:MAX') {
            errorActions(medicaidResponse, 'medicaidValidation');
          } else {
            handleMedicaidError(
              errorMessages.getRow(
                medicaidResponse?.error?.code || 'MEDICAID_DEFAULT'
              )?.message
            );
          }
          return;
        }
        handleMedicaidError(false);
        handleNext('medicaid validation', payload);
      });
    });
  };
  const operatePaymentInfo = () => {
    setNonFormSpecificError(false);
    setErrMessage('');
    form.validate('PAASelfAttestation.paymentRefNumber');
    form.validate('PAASelfAttestation.totalAmPaid');

    form.validate(
      'PAASelfAttestation',
      (formModel, fields) => {
        adobeTaggingEvent('VERIFY_CLAIM', {
          verificationType: 'payment verification',
        });
        const { paymentRefNumber, totalAmPaid: amountPaid } = formModel;
        const seriesDesignator = formModel.seriesDesCode || '';
        const payload = {
          tin: TINNumber,
          requestId: TINInfo.requestId,
          paymentRefNumber,
          seriesDesignator,
          amountPaid: parseDollarToNumber(amountPaid),
          currentFlow,
          url:
            uiMessages.getRow('REGISTER_API')?.message ||
            config('REGISTER_API'),
        };

        verifyPaymentInfo(
          payload,
          (paymentResponse) => {
            if (paymentResponse.error) {
              if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
                adobeTaggingEvent('API_TRANSACTION_ERROR', {
                  errorMessage: paymentResponse.error.description,
                  errorApiCode: paymentResponse.error.code,
                  errorHttpsCode: paymentResponse.error.status,
                });
              }
              form.setValue('PAASelfAttestation', {
                paymentRefNumber: '',
                totalAmPaid: '',
                seriesDesCode: '',
              });
              errorActions(paymentResponse);
              return;
            }
            handleNext('payment information', payload);
          },
          (err) => {
            form.setValue('PAASelfAttestation', {
              paymentRefNumber: '',
              totalAmPaid: '',
              seriesDesCode: '',
            });
            errorActions(err);
          }
        );
      },
      (error) => {
        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const fields = [];
          if (error.paymentRefNumber === '') {
            fields.paymentRefNumber = '';
            fields.push('payment reference number');
          }
          if (error.totalAmPaid === '' || error.totalAmPaid === '$0.00') {
            fields.push('total amount paid');
          }
          if (fields.length > 0) {
            adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
          }
        }
      }
    );
  };

  const operateUsMailValidation = () => {
    handleError(false);
    if (showSecurityInput === 'Yes') {
      form.validate('PAASelfAttestation.securityKeyInput');
      form.validate(
        'PAASelfAttestation',
        (formModel, fields) => {
          const { securityKey, securityKeyInput } = formModel;
          const payload = {
            requestId: TINInfo.requestId,
            currentFlow,
            key: securityKey === 'No' ? '' : securityKeyInput,
            url:
              uiMessages.getRow('REGISTER_API')?.message ||
              config('REGISTER_API'),
          };

          validateSecurityKey(
            payload,
            (response) => {
              if (response.error) {
                if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
                  adobeTaggingEvent('API_TRANSACTION_ERROR', {
                    errorMessage: response.error.description,
                    errorApiCode: response.error.code,
                    errorHttpsCode: response.error.status,
                  });
                }
                form.setValue('PAASelfAttestation', {
                  securityKeyInput: '',
                });
                errorActions(response, 'UsMailValidation');
                handleError(true);
                return;
              }
              if (
                typeof response.keyMatch !== 'undefined' &&
                !response.keyMatch
              ) {
                handleError(true);
                return;
              }
              if (
                Object.keys(response).length === 0 &&
                showSecurityInput === 'No'
              ) {
                router.navigate(`/PAARegistration/waitingView/${token}`);
                handleStopAll();
                return;
              }
              handleNext('us mail validation', payload);
            },
            (err) => {
              form.setValue('PAASelfAttestation', {
                securityKeyInput: '',
              });
              handleError(true);
              errorActions(err, 'UsMailValidation');
            }
          );
          return false;
        },
        (error) => {
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            const fields = [];
            if (error.securityKey === 'Yes' && error.securityKeyInput === '') {
              fields.push('Security Key');
            }
            if (fields.length > 0) {
              adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
            }
          }
        }
      );
    } else if (showSecurityInput === 'No') {
      form.validate('PAASelfAttestation.mailAddress');
      form.validate(
        'PAASelfAttestation',
        (formModel, fields) => {
          const { mailAddress } = formModel;
          const payload = {
            requestId: TINInfo.requestId,
            addressId: mailAddress,
            currentFlow,
            url:
              uiMessages.getRow('REGISTER_API')?.message ||
              config('REGISTER_API'),
          };
          generateSecurityKey(
            payload,
            (response) => {
              if (response.error) {
                if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
                  adobeTaggingEvent('API_TRANSACTION_ERROR', {
                    errorMessage: response.error.description,
                    errorApiCode: response.error.code,
                    errorHttpsCode: response.error.status,
                  });
                }
                errorActions(response);
                handleError(true);
              }
              if (
                Object.keys(response).length === 0 &&
                showSecurityInput === 'No'
              ) {
                router.navigate(`/PAARegistration/waitingView/${token}`);
                handleDisableCancel();
                resetState();
                clearClaimInformation();
              }
            },
            (err) => {
              handleError(true);
              errorActions(err);
            }
          );
        },
        (error) => {
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            const fields = [];
            if (!error.mailAddress || error.mailAddress === '') {
              fields.push('Mail Address');
            }
            if (fields.length > 0) {
              adobeTaggingEvent('FORM_VALIDATION_ERROR', { fields });
            }
          }
        }
      );
    }
  };

  const handleChooseAnotherMethod = () => {
    handleMedicaidError('');
    handleError(false);
    setPaymentRefNumberError('');
    setTotalAmPaidError('');
    setPaymentInfoNotMatchedError('');
    setNonFormSpecificError('');
    setAddressApiError('');
    setGenerateKeyError('');
    setErrMessage('');
    clearClaimInformation();
    setReduxIsProcedureCode('');
    setMainRadioGroup('');
  };
  const handleAddressAPICall = () => {
    handleError(false);
    const payload = {
      requestId: TINInfo.requestId,
      currentFlow,
      url: uiMessages.getRow('REGISTER_API')?.message || config('REGISTER_API'),
      isDental,
    };

    tinAddressApi(
      payload,
      (response) => {
        if (response.error) {
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            adobeTaggingEvent('API_TRANSACTION_ERROR', {
              errorMessage: response.error.description,
              errorApiCode: response.error.code,
              errorHttpsCode: response.error.status,
            });
          }
          errorActions(response);
          handleError(true);
        }
      },
      (err) => {
        handleError(true);
        errorActions(err);
      }
    );
    return false;
  };

  const handleChangeRadio = (e) => {
    setRadioGroup(e.target.value);
  };

  const handleRadioContinue = () => {
    adobeInteraction(
      'company verification',
      'continue',
      'provider',
      featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active,
      'company verification'
    );
    if (radioGroup === 'usMailValidation') {
      adobeTaggingEvent('MODAL_OPEN', {
        modalName:
          'have you received the security key in the mail already from unitedhealthcare',
      });
      setShowSecurityModal(true);
    } else {
      setMainRadioGroup(radioGroup);
    }
  };

  const handleSecurityNoChange = () => {
    setShowSecurityModal(false);
    setMainRadioGroup(radioGroup);
    form.setValue('PAASelfAttestation.securityKey', reduxUsMailSecurityRadio);
    setShowSecurityInput('No');
    setUSMailSecurityRadioGroup('No');
    handleAddressAPICall();
  };

  const handleSecurityYesChange = () => {
    setShowSecurityModal(false);
    setMainRadioGroup(radioGroup);
    form.setValue('PAASelfAttestation.securityKey', reduxUsMailSecurityRadio);
    setShowSecurityInput('Yes');
    setUSMailSecurityRadioGroup('Yes');
  };

  const handleSubmit = () => {
    clearClaimInformation();
    // setReduxIsProcedureCode(''); // Note: this was in the old code but was causing first submit to always clear info without submitting
    const functionalObj = {
      claimMemberInfo: operateClaimMemberInfo,
      paymentInfo: operatePaymentInfo,
      usMailValidation: operateUsMailValidation,
      medicaidIdInfo: operateMedicaidInfo,
    };
    functionalObj[reduxMainRadio]();
  };

  const [securityKey, setSecurityKey] = useState('');

  const [paymentRefNumber, setPaymentRefNumber] = useState('');
  const [seriesDesCode, setSeriesDesCode] = useState('');
  const [totalAmPaid, setTotalAmPaid] = useState('');

  const [patientDOB, setPatientDOB] = useState('');
  const [claim, setClaim] = useState('');
  const [procedureCode, setProcedureCode] = useState('');
  const [revenueCode, setRevenueCode] = useState('');

  const [fDOS, setFDOS] = useState('');
  const [lDOS, setLDOS] = useState('');

  const handleProcedureRadioChange = (e) => {
    setProcedureCode('');
    setRevenueCode('');
    actions.updateClaimInfo({
      procedureCode: '',
      revenueCode: '',
    });
    setIsProcedureCode(e.target.value);
    setReduxIsProcedureCode(e.target.value);
  };
  const handleInfoModelClose = () => {
    setInfoModelOpen(false);
    setValidationErrorType('');
    setModalErrorCode('');
  };

  const customCurrencyFormatter = (value) => {
    // // Remove all non-numeric characters
    let numericValue = value.replace(/\D/g, '');

    // If there are no numeric characters, return an empty string
    if (numericValue === '') {
      return '';
    }

    // Ensure at least two digits (for cents)
    numericValue = numericValue.padStart(2, '0');

    // Insert a decimal point two digits from the end
    const cents = numericValue.slice(-2);
    const integerPart = numericValue.slice(0, -2) || '0';
    const formattedIntegerPart = parseInt(integerPart, 10);

    // Combine the integer part with the cents part
    return `${formattedIntegerPart}.${cents}`;
  };

  return (
    <React.Fragment>
      <ValidationInfoModal
        isOpen={infoModelOpen}
        closeModal={handleInfoModelClose}
        errorCode={modalErrorCode}
      />
      {reduxMainRadio === '' && (
        <React.Fragment>
          <MainContainer>
            <BoxHeaderText>
              Verify your organization's information.
            </BoxHeaderText>
            <Flex>
              <RadioGroup
                id="varifyRadio"
                label="Please select method of verification."
                onChange={handleChangeRadio}
                display="row"
                defaultValue="claimMemberInfo"
                value={radioGroup}
                css={{
                  'abyss-radio-group-label': {
                    fontWeight: 10,
                  },
                }}
              >
                <HorizontalView>
                  <LayoutStyle
                    onClick={() => {
                      return setRadioGroup('claimMemberInfo');
                    }}
                  >
                    <InLine>
                      <IconBrand
                        size="50px"
                        variant="onetone"
                        icon="clipboard"
                      />
                      <RadioGroup.Radio
                        data-testid="verifyClaim-check-claimmember-info"
                        value="claimMemberInfo"
                        css={{
                          'abyss-radio-root': {
                            alignItems: 'center',
                            marginLeft: 'auto',
                          },
                        }}
                      />
                    </InLine>
                    <LayoutAllign>
                      <LayoutHeader>Claim and Member Information</LayoutHeader>
                      <LayoutInstruction>
                        Enter information from a claim submitted to
                        UnitedHealthcare within the last 60 days.
                      </LayoutInstruction>
                    </LayoutAllign>
                  </LayoutStyle>
                  {TINInfo?.medicaidMatch && (
                    <LayoutStyle
                      onClick={() => {
                        return setRadioGroup('medicaidIdInfo');
                      }}
                    >
                      <InLine>
                        <IconSymbol
                          icon="badge"
                          size="39px"
                          variant="outlined"
                          color="$primary1"
                        />
                        <RadioGroup.Radio
                          data-testid="verifyMedicaid-id-info"
                          value="medicaidIdInfo"
                          css={{
                            'abyss-radio-root': {
                              alignItems: 'center',
                              marginLeft: 'auto',
                            },
                          }}
                        />
                      </InLine>
                      <LayoutAllign>
                        <LayoutHeader>
                          Medicaid Provider ID Validation
                        </LayoutHeader>
                        <LayoutInstruction>
                          Enter your state-issued Medicaid Provider ID.
                        </LayoutInstruction>
                      </LayoutAllign>
                    </LayoutStyle>
                  )}
                  {(!isAPIMarketPlaceUser ||
                    (isAPIMarketPlaceUser && !isDental)) && (
                    <LayoutStyle
                      onClick={() => {
                        return setRadioGroup('paymentInfo');
                      }}
                    >
                      <InLine>
                        <IconBrand
                          size="50px"
                          variant="onetone"
                          icon="bank_card"
                        />
                        <RadioGroup.Radio
                          data-testid="verifyClaim-check-payment-info"
                          value="paymentInfo"
                          css={{
                            'abyss-radio-root': {
                              alignItems: 'center',
                              marginLeft: 'auto',
                            },
                          }}
                        />
                      </InLine>
                      <LayoutAllign>
                        <LayoutHeader>Payment Information</LayoutHeader>
                        <LayoutInstruction>
                          Enter information from a claim paid by
                          UnitedHealthcare in the last 60 days.
                          {featureToggles.getRow('US6041711_AARP_PAYMENT_ALERT')
                            ?.active && (
                            <LayoutNeedText>
                              Payment verification is not available for medicare
                              supplement payments.
                            </LayoutNeedText>
                          )}
                        </LayoutInstruction>
                      </LayoutAllign>
                    </LayoutStyle>
                  )}
                  <LayoutStyle
                    onClick={() => {
                      return setRadioGroup('usMailValidation');
                    }}
                  >
                    <InLine>
                      <IconBrand size="50px" variant="onetone" icon="mail" />
                      <RadioGroup.Radio
                        data-testid="verifyClaim-check-USMail-validation"
                        value="usMailValidation"
                        css={{
                          'abyss-radio-root': {
                            alignItems: 'center',
                            marginLeft: 'auto',
                          },
                        }}
                      />
                    </InLine>
                    <LayoutAllign>
                      <LayoutHeader>US Mail Validation</LayoutHeader>
                      <LayoutInstruction>
                        Request or enter a Security Key from UnitedHealthcare
                        via US mail service.
                      </LayoutInstruction>
                    </LayoutAllign>
                  </LayoutStyle>
                </HorizontalView>
              </RadioGroup>
            </Flex>
          </MainContainer>
          <Flex justify="space-between" width="140%">
            <Button
              variant="outline"
              onClick={() => handleBack('company verification')}
              before={<IconSymbol icon="arrow_back" color="$primary1" />}
              css={{
                'abyss-button-root': {
                  marginLeft: '-280px',
                },
              }}
            >
              Back
            </Button>
            <Button
              onClick={handleRadioContinue}
              isDisabled={radioGroup === ''}
              after={<IconSymbol icon="arrow_forward" color="$primary2" />}
              css={{
                'abyss-button-root': {
                  marginRight: '-280px',
                },
              }}
            >
              Continue
            </Button>
          </Flex>
        </React.Fragment>
      )}
      <Modal
        isOpen={showSecurityModal}
        showCloseButton
        onClose={() => {
          adobeTaggingEvent('MODAL_CLOSE', {
            modalName:
              'have you received the security key in the mail already from unitedhealthcare',
            linkName: 'close',
          });
          return setShowSecurityModal(false);
        }}
      >
        <Modal.Section>
          <LayoutModal>
            <LayoutHeader>
              Have you received the Security Key in the mail already from
              UnitedHealthcare?
            </LayoutHeader>
            <Flex justify="end">
              <Button
                variant="outline"
                onClick={() => {
                  adobeTaggingEvent('MODAL_CLOSE', {
                    modalName:
                      'have you received the security key in the mail already from unitedhealthcare',
                    linkName: 'no',
                  });
                  handleSecurityNoChange();
                }}
              >
                No
              </Button>
              <Button
                css={{
                  'abyss-button-root': {
                    marginLeft: '10px',
                  },
                }}
                onClick={() => {
                  adobeTaggingEvent('MODAL_CLOSE', {
                    modalName:
                      'have you received the security key in the mail already from unitedhealthcare',
                    linkName: 'yes',
                  });
                  handleSecurityYesChange();
                }}
              >
                Yes
              </Button>
            </Flex>
          </LayoutModal>
        </Modal.Section>
      </Modal>
      <FormProvider state={form} onSubmit={form.handleSubmit(handleSubmit)}>
        {reduxMainRadio !== '' && (
          <React.Fragment>
            {(() => {
              const displayObj = {
                claimMemberInfo: () => {
                  return (
                    <div>
                      <MainContainer>
                        <BoxHeaderText>
                          Entering Claim Information
                        </BoxHeaderText>
                        <LayoutInstruction>
                          Enter information from a claim submitted to
                          UnitedHealthcare{' '}
                          <LayoutNeedText>
                            {' '}
                            within the last 60 days.
                          </LayoutNeedText>
                        </LayoutInstruction>
                        <div
                          style={{
                            borderRadius: '4px',
                            marginBottom: '5px',
                            padding: '5px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                            }}
                          >
                            <div style={{ flex: 1, marginRight: '20px' }}>
                              <TextInput
                                id="memberId"
                                model="PAASelfAttestation.verifyClaimMemberId"
                                label="Member ID"
                                type="number"
                                aria-label="Member Id"
                                validators={{
                                  required: 'Please enter a valid Member ID',
                                }}
                                formatter={(value) => {
                                  return value.replace(
                                    /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                    ''
                                  );
                                }}
                                onChange={(e) => {
                                  setMemIdErr(false);
                                  actions.updateClaimInfo({
                                    memberId: e.target.value,
                                  });
                                }}
                                css={{
                                  'abyss-text-input-label': {
                                    fontWeight: 'bold',
                                  },
                                  'abyss-text-input': {
                                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button':
                                      {
                                        '-webkit-appearance': 'none',
                                      },
                                    '[type=number]': {
                                      '-webkit-appearance': 'none',
                                    },
                                    '[type="number"]': {
                                      '-webkit-appearance': 'textfield',
                                      '-moz-appearance': 'textfield',
                                      appearance: 'textfield',
                                    },
                                  },
                                }}
                                isClearable
                                maxLength={30}
                                required
                              />

                              {memIdErr ? (
                                <div
                                  style={{
                                    color: 'rgb(208 2 28)',
                                    fontSize: '12px',
                                  }}
                                >
                                  {errMessage}
                                </div>
                              ) : null}
                            </div>
                            <div style={{ flex: 1 }}>
                              <DateInput
                                id="patientDOB"
                                model="PAASelfAttestation.verifyClaimPatientDOB"
                                required
                                label="Patient Date of Birth"
                                aria-label="Patient date of birth"
                                validators={{
                                  required: 'Please enter a Date of Birth',
                                  validate: {
                                    futureDate: (value) => {
                                      if (
                                        value &&
                                        new Date(value) < MAX_BIRTH_DATE
                                      ) {
                                        return true;
                                      }
                                      return 'You cannot enter a future Date of Birth';
                                    },
                                    pastDate: (value) => {
                                      if (
                                        value &&
                                        new Date(value) > MIN_BIRTH_DATE
                                      ) {
                                        return true;
                                      }
                                      return 'Please enter a valid Date of Birth';
                                    },
                                  },
                                }}
                                onChange={(date) => {
                                  setPatientDOB(date);
                                  actions.updateClaimInfo({
                                    patientDob: date,
                                  });
                                }}
                                isClearable
                                value={patientDOB}
                                onFocus={() => {
                                  return setPatientDOBError(false);
                                }}
                                css={{
                                  'abyss-date-picker-input-label': {
                                    fontWeight: 'bold',
                                  },
                                  'abyss-date-picker-input-descriptors': {
                                    display: 'none',
                                  },
                                }}
                              />

                              {patientDOBError ? (
                                <div
                                  style={{
                                    color: 'rgb(208 2 28)',
                                    fontSize: '12px',
                                  }}
                                >
                                  {errMessage}
                                </div>
                              ) : null}
                            </div>
                            <div style={{ flex: 1, marginLeft: '20px' }}>
                              <TextInput
                                id="claim"
                                model="PAASelfAttestation.verifyClaimPatienClaim"
                                required
                                label="Claim Number"
                                aria-label="Claim Number"
                                validators={{
                                  required: 'Please enter a valid Claim',
                                }}
                                formatter={(value) => {
                                  return value.replace(
                                    /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                    ''
                                  );
                                }}
                                onChange={(e) => {
                                  setClaim(e.target.value);
                                  actions.updateClaimInfo({
                                    claimNumber: e.target.value,
                                  });
                                }}
                                isClearable
                                value={claim}
                                onFocus={() => {
                                  return setClaimError(false);
                                }}
                                maxLength={30}
                                css={{
                                  'abyss-text-input-label': {
                                    fontWeight: 'bold',
                                  },
                                }}
                              />
                              {claimError ? (
                                <div
                                  style={{
                                    color: 'rgb(208 2 28)',
                                    fontSize: '12px',
                                  }}
                                >
                                  {errMessage}
                                </div>
                              ) : null}
                            </div>
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              flexWrap: 'wrap',
                              marginTop: '20px',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div id="fdos-input">
                                <DateInput
                                  id="fDOS"
                                  model="PAASelfAttestation.fDOS"
                                  required
                                  label="First Date of Service"
                                  aria-label="First Date of Service"
                                  validators={{
                                    required:
                                      'Please enter a valid First Date of Service',
                                  }}
                                  onChange={(date) => {
                                    setFDOS(date);
                                    actions.updateClaimInfo({
                                      fDOS: date,
                                    });
                                  }}
                                  isClearable
                                  value={fDOS}
                                  css={{
                                    'abyss-date-picker-input-label': {
                                      fontWeight: 'bold',
                                    },
                                    'abyss-date-picker-input-descriptors': {
                                      display: 'none',
                                    },
                                  }}
                                />

                                {lDOSError ? (
                                  <div
                                    style={{
                                      color: 'rgb(208 2 28)',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {errMessage}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <div style={{ flex: 1, marginLeft: '20px' }}>
                              <DateInput
                                id="lDOS"
                                model="PAASelfAttestation.lDOS"
                                required
                                label="Last Date of Service"
                                aria-label="lDOS"
                                validators={{
                                  required: 'Last Date of Service is required',
                                  validate: {
                                    beforeLast: (value) => {
                                      if (
                                        Number.isNaN(new Date(fDOS).getTime())
                                      ) {
                                        return true;
                                      }

                                      return (
                                        startOfDay(new Date(value)) >=
                                          startOfDay(new Date(fDOS)) ||
                                        'Last Date of Service must be after First Date of Service'
                                      );
                                    },
                                  },
                                }}
                                onChange={(date) => {
                                  setLDOS(date);
                                  actions.updateClaimInfo({
                                    lDOS: date,
                                  });
                                }}
                                isClearable
                                value={lDOS}
                                css={{
                                  'abyss-date-picker-input-label': {
                                    fontWeight: 'bold',
                                  },
                                  'abyss-date-picker-input-descriptors': {
                                    display: 'none',
                                  },
                                }}
                              />
                            </div>
                            {fDOSError ? (
                              <div
                                style={{
                                  color: 'rgb(208 2 28)',
                                  fontSize: '12px',
                                }}
                              >
                                {errMessage}
                              </div>
                            ) : null}
                            <div style={{ flex: 1, marginLeft: '20px' }}>
                              {isAPIMarketPlaceUser && isDental && (
                                <React.Fragment>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      flexWrap: 'wrap',
                                    }}
                                  >
                                    <div style={{ flex: 1 }}>
                                      <TextInput
                                        label="Procedure Code"
                                        id="procedureCodeInput"
                                        model="PAASelfAttestation.verifyClaimprocedureCode"
                                        aria-label="Procedure Code"
                                        validators={{
                                          required:
                                            isProcedureCode === 'revenueCode'
                                              ? false
                                              : 'Please enter a valid Procedure code',
                                        }}
                                        formatter={(value) => {
                                          return value.replace(
                                            /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                            ''
                                          );
                                        }}
                                        onChange={(e) => {
                                          setProcedureCode(e.target.value);
                                          actions.updateClaimInfo({
                                            procedureCode: e.target.value,
                                          });
                                          setIsProcedureCode('procedureCode');
                                        }}
                                        onClear={() => {
                                          setProcedureCode('');
                                          actions.updateClaimInfo({
                                            procedureCode: '',
                                          });
                                        }}
                                        isClearable
                                        value={procedureCode}
                                        onFocus={() => {
                                          return setProcedureCodeErr(false);
                                        }}
                                        maxLength={20}
                                      />
                                      {procedureCodeErr ? (
                                        <div
                                          style={{
                                            color: 'rgb(208 2 28)',
                                            fontSize: '12px',
                                          }}
                                        >
                                          {errMessage}
                                        </div>
                                      ) : null}
                                    </div>
                                  </div>
                                </React.Fragment>
                              )}
                            </div>
                          </div>
                          {(!isAPIMarketPlaceUser ||
                            (isAPIMarketPlaceUser && !isDental)) && (
                            <RadioGroup
                              label="Enter one of the following"
                              model="radio-form"
                              validators={{
                                required: !isProcedureCode,
                              }}
                              css={{
                                'abyss-radio-group-label': {
                                  fontWeight: 300,
                                },
                                'abyss-radio-group-root': {
                                  marginTop: '20px',
                                  marginBottom: 'px',
                                },
                              }}
                            >
                              <RadioGroup
                                data-testid="claimMember-check"
                                onChange={handleProcedureRadioChange}
                                display="row"
                                value={isProcedureCode}
                              >
                                <Flex display="inline" css={{ width: '100%' }}>
                                  <RadioGroup.Radio
                                    data-testid="procedureCode-check"
                                    value="procedureCode"
                                    label="Procedure Code"
                                    css={{
                                      'abyss-radio-label': {
                                        fontWeight: 'bold',
                                      },
                                    }}
                                  />
                                  <RadioGroup.Radio
                                    data-testid="revenueCode-check"
                                    value="revenueCode"
                                    label="Revenue Code"
                                    css={{
                                      'abyss-radio-root': {
                                        marginLeft: '21%',
                                      },
                                      'abyss-radio-label': {
                                        fontWeight: 'bold',
                                      },
                                    }}
                                  />
                                </Flex>
                                <Flex display="inline" css={{ width: '100%' }}>
                                  <TextInput
                                    id="procedureCodeInput"
                                    model="PAASelfAttestation.verifyClaimprocedureCode"
                                    isDisabled={
                                      isProcedureCode === 'revenueCode'
                                    }
                                    validators={{
                                      required:
                                        isProcedureCode === 'revenueCode'
                                          ? false
                                          : 'Please enter a valid Procedure code',
                                    }}
                                    formatter={(value) => {
                                      return value.replace(
                                        /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                        ''
                                      );
                                    }}
                                    css={{
                                      'abyss-text-input-root': {
                                        width: '32.3%',
                                        marginTop: '10px',
                                      },
                                      'abyss-text-input-label': {
                                        display: 'none',
                                      },
                                    }}
                                    onChange={(e) => {
                                      setProcedureCode(e.target.value);
                                      actions.updateClaimInfo({
                                        procedureCode: e.target.value,
                                      });
                                      setIsProcedureCode('procedureCode');
                                    }}
                                    onClear={() => {
                                      setProcedureCode('');
                                      actions.updateClaimInfo({
                                        procedureCode: '',
                                      });
                                    }}
                                    isClearable
                                    value={procedureCode}
                                    onFocus={() => {
                                      return setProcedureCodeErr(false);
                                    }}
                                    maxLength={20}
                                  />
                                  {procedureCodeErr ? (
                                    <div
                                      style={{
                                        color: 'rgb(208 2 28)',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {errMessage}
                                    </div>
                                  ) : null}
                                  <TextInput
                                    id="revenueCodeInput"
                                    model="PAASelfAttestation.verifyClaimrevenueCode"
                                    isDisabled={
                                      isProcedureCode === 'procedureCode'
                                    }
                                    validators={{
                                      required:
                                        isProcedureCode === 'procedureCode'
                                          ? false
                                          : 'Please enter a valid Revenue code',
                                    }}
                                    formatter={(value) => {
                                      return value.replace(
                                        /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                        ''
                                      );
                                    }}
                                    css={{
                                      'abyss-text-input-root': {
                                        width: '32.3%',
                                        marginLeft: '20px',
                                      },
                                      'abyss-text-input-label': {
                                        display: 'none',
                                      },
                                    }}
                                    onChange={(e) => {
                                      setRevenueCode(e.target.value);
                                      actions.updateClaimInfo({
                                        revenueCode: e.target.value,
                                      });
                                      setIsProcedureCode('revenueCode');
                                    }}
                                    onClear={() => {
                                      setRevenueCode('');
                                      actions.updateClaimInfo({
                                        revenueCode: '',
                                      });
                                    }}
                                    isClearable
                                    value={revenueCode}
                                    onFocus={() => {
                                      return setRevenueCodeErr(false);
                                    }}
                                    maxLength={20}
                                  />
                                  {revenueCodeErr ? (
                                    <div
                                      style={{
                                        color: 'rgb(208 2 28)',
                                        fontSize: '12px',
                                      }}
                                    >
                                      {errMessage}
                                    </div>
                                  ) : null}
                                </Flex>
                              </RadioGroup>
                            </RadioGroup>
                          )}
                        </div>
                      </MainContainer>
                      <Flex justify="space-between" width="140%">
                        <Button
                          variant="outline"
                          before={
                            <IconSymbol icon="arrow_back" color="$primary1" />
                          }
                          onClick={handleChooseAnotherMethod}
                          css={{
                            'abyss-button-root': {
                              marginLeft: '-280px',
                            },
                          }}
                        >
                          Choose another method
                        </Button>
                        <Button
                          type="submit"
                          after={
                            <IconSymbol
                              icon="arrow_forward"
                              color="$primary2"
                            />
                          }
                          css={{
                            'abyss-button-root': {
                              marginRight: '-280px',
                            },
                          }}
                        >
                          Next
                        </Button>
                      </Flex>
                    </div>
                  );
                },
                paymentInfo: () => {
                  return (
                    <React.Fragment>
                      <MainContainer>
                        <BoxHeaderText>
                          Entering Payment Information
                        </BoxHeaderText>
                        <HelpText>
                          <LayoutNeedText>
                            If the payment number on your PRA has two strings
                            separated by a space:
                          </LayoutNeedText>{' '}
                          enter the first string as the series designator code
                          and second string as the payment reference number.
                        </HelpText>
                        <HelpText>
                          <LayoutNeedText>
                            If the payment number on your PRA is one string with
                            no space:
                          </LayoutNeedText>{' '}
                          enter the entire string as the payment reference
                          number and leave the series designator code box blank.{' '}
                        </HelpText>
                        <HelpText>
                          Enter information from a claim paid by
                          UnitedHealthcare{' '}
                          <LayoutNeedText>
                            within the last 60 days.
                          </LayoutNeedText>
                          {featureToggles.getRow('US6041711_AARP_PAYMENT_ALERT')
                            ?.active && (
                            <React.Fragment>
                              Payment verification is not available for Medicare
                              supplement payments.
                            </React.Fragment>
                          )}
                        </HelpText>
                        <PaymentInfoHorizontalView>
                          <TextInput
                            id="seriesDesCode"
                            model="PAASelfAttestation.seriesDesCode"
                            label="Series Designator Code (Optional)"
                            aria-label="Series Designator Code"
                            formatter={(value) => {
                              return value.replace(
                                /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/?]/g,
                                ''
                              );
                            }}
                            onChange={(e) => {
                              setPaymentInfoNotMatchedError('');
                              setSeriesDesCode(e.target.value);
                            }}
                            onClear={(e) => {
                              setPaymentInfoNotMatchedError('');
                              setErrMessage('');
                            }}
                            css={{
                              'abyss-text-input-label': {
                                fontSize: '16px',
                                fontWeight: 'bold',
                              },
                              'abyss-text-input-root': {
                                marginLeft: '1%',
                                marginRight: '1%',
                                marginTop: '0.75%',
                              },
                            }}
                            isClearable
                            value={seriesDesCode}
                            // subText="optional"
                            maxLength={20}
                          />
                          <TextInput
                            id="paymentRefNumber"
                            model="PAASelfAttestation.paymentRefNumber"
                            label="Payment Reference Number"
                            aria-label="Payment Reference Number"
                            formatter={(value) => {
                              return value.replace(
                                /[!@#$%^&*)({}<>,.[\]\-+_`~=\\|';":/? ]/g,
                                ''
                              );
                            }}
                            validators={{
                              required:
                                'Please enter a valid Payment Reference Number',
                              validate: {
                                allZeros: (value) => {
                                  return (
                                    !value.match(/^[0]+$/) ||
                                    'Please enter a valid Payment Reference Number'
                                  );
                                },
                              },
                            }}
                            onChange={(e) => {
                              setPaymentRefNumberError('');
                              setPaymentInfoNotMatchedError('');
                              setPaymentRefNumber(e.target.value);
                            }}
                            onClear={(e) => {
                              setPaymentRefNumberError('');
                              setPaymentInfoNotMatchedError('');
                            }}
                            css={{
                              'abyss-text-input-label': {
                                fontSize: '16px',
                                fontWeight: 'bold',
                              },
                              'abyss-text-input-root': {
                                marginLeft: '1%',
                                marginRight: '1%',
                              },
                            }}
                            isClearable
                            value={paymentRefNumber}
                            maxLength={20}
                          />

                          {paymentRefNumberError ? (
                            <div
                              style={{
                                color: 'rgb(208 2 28)',
                                fontSize: '12px',
                              }}
                            >
                              {paymentRefNumberError}
                            </div>
                          ) : null}

                          <TextInput
                            id="totalAmPaid"
                            model="PAASelfAttestation.totalAmPaid"
                            label="Total Amount Paid"
                            aria-label="Total Amount Paid"
                            mask="numeric"
                            maskConfig={{
                              thousandSeparator: ',',
                              prefix: '$',
                            }}
                            formatter={customCurrencyFormatter}
                            validators={{
                              required: 'Total Amount Paid is required',
                              validate: {
                                minValue: (val) => {
                                  return (
                                    +parseDollarToNumber(val) >= 0 ||
                                    'Amount must be greater than $0.00'
                                  );
                                },
                                maxValue: (val) => {
                                  return (
                                    +parseDollarToNumber(val) < 1000000.01 ||
                                    'Amount can not exceed $1,000,000'
                                  );
                                },
                              },
                            }}
                            onChange={(e) => {
                              setTotalAmPaidError('');
                              setPaymentInfoNotMatchedError('');
                              setTotalAmPaid(e.target.value);
                              form.validate('PAASelfAttestation.totalAmPaid');
                            }}
                            onClear={(e) => {
                              setTotalAmPaidError('');
                              setPaymentInfoNotMatchedError('');
                            }}
                            css={{
                              'abyss-text-input-label': {
                                fontSize: '16px',
                                fontWeight: 'bold',
                              },
                            }}
                            isClearable
                            value={totalAmPaid}
                            maxLength={12}
                          />
                          {totalAmPaidError ? (
                            <div
                              style={{
                                color: 'rgb(208 2 28)',
                                fontSize: '12px',
                              }}
                            >
                              {totalAmPaidError}
                            </div>
                          ) : null}
                        </PaymentInfoHorizontalView>
                        <HelpLink
                          onClick={() => {
                            return setShowHelpModal(true);
                          }}
                        >
                          Where can I find this?
                        </HelpLink>
                        {paymentInfoNotMatchedError ? (
                          <p
                            style={{
                              color: 'rgb(208 2 28)',
                              fontSize: '80%',
                              fontWeight: '600',
                            }}
                          >
                            {paymentInfoNotMatchedError}
                          </p>
                        ) : null}
                        {nonFormSpecificError ? (
                          <p
                            style={{
                              color: 'rgb(208 2 28)',
                              fontSize: '80%',
                              fontWeight: '600',
                            }}
                          >
                            {nonFormSpecificError}
                          </p>
                        ) : null}
                        <PaymentInfoHelpModal
                          show={showHelpModal}
                          onClose={() => {
                            return setShowHelpModal(false);
                          }}
                        />
                      </MainContainer>
                      <Flex justify="space-between" width="140%">
                        <Button
                          variant="outline"
                          before={
                            <IconSymbol icon="arrow_back" color="$primary1" />
                          }
                          onClick={handleChooseAnotherMethod}
                          css={{
                            'abyss-button-root': {
                              marginLeft: '-280px',
                            },
                          }}
                        >
                          Choose another method
                        </Button>
                        <Button
                          type="submit"
                          after={
                            <IconSymbol
                              icon="arrow_forward"
                              color="$primary2"
                            />
                          }
                          css={{
                            'abyss-button-root': {
                              marginRight: '-280px',
                            },
                          }}
                        >
                          Next
                        </Button>
                      </Flex>
                    </React.Fragment>
                  );
                },
                usMailValidation: () => {
                  return (
                    <USMailValidationView>
                      {(() => {
                        if (showSecurityInput === 'Yes') {
                          return (
                            <SecurityContainer>
                              <BoxHeaderText>US Mail Validation</BoxHeaderText>
                              <HelpText>
                                Enter the Security Key you have received from
                                UnitedHealthcare by US mail.
                              </HelpText>
                              <div
                                style={{
                                  width: '35%',
                                  marginTop: '10px',
                                  padding: '10px',
                                }}
                              >
                                <TextInput
                                  id="securityKeyInput"
                                  model="PAASelfAttestation.securityKeyInput"
                                  label="Security Key"
                                  aria-label="Security Key"
                                  validators={{
                                    required: 'Security Key is required',
                                  }}
                                  onChange={(e) => {
                                    setSecurityKey(e.target.value);
                                  }}
                                  value={securityKey}
                                  isClearable
                                  css={{
                                    'abyss-text-input-label': {
                                      fontWeight: 'bold',
                                    },
                                  }}
                                  maxLength={20}
                                />
                              </div>
                            </SecurityContainer>
                          );
                        }

                        return null;
                      })()}
                      {(() => {
                        if (showSecurityInput === 'No') {
                          return (
                            <SecurityContainer>
                              <BoxHeaderText>US Mail Validation</BoxHeaderText>
                              <HelpText>
                                Please select the address on file that you would
                                like the Security Key sent to. Allow 10 business
                                days for this to be delivered.
                              </HelpText>
                              <div style={{ width: '35%', padding: '10px' }}>
                                <SelectInput
                                  id="securityKeyMailAddress"
                                  model="PAASelfAttestation.mailAddress"
                                  label="Select an Address on File"
                                  aria-label="Select an Address on File"
                                  aria-required
                                  validators={{
                                    required: 'Please Select an Address',
                                  }}
                                  css={{
                                    'abyss-select-input-label': {
                                      fontWeight: 'bold',
                                      fontSize: '14px',
                                    },
                                  }}
                                  required
                                  options={getOptions()}
                                  placeholder="Begin typing to search"
                                  isSearchable
                                />
                                {!isAPIMarketPlaceUser && (
                                  <Layout.Group>
                                    <HelpText>
                                      {`Missing an address? `}{' '}
                                    </HelpText>
                                    <Popover
                                      title="Missing an address?"
                                      onClick={() => {
                                        adobeTaggingEvent('MAIL_VALIDATION', {
                                          interactionName: 'US Mail Validation',
                                          interactionValue:
                                            'Missing an address:check info',
                                          interactionContext:
                                            'company verification',
                                        });
                                      }}
                                      content={`Use the chat icon in the lower right-hand corner. Please select “Provider Onboarding” and then "Demographics" to get an address update started.`}
                                    />
                                  </Layout.Group>
                                )}
                                {addressApiError ? (
                                  <div
                                    style={{
                                      color: 'rgb(208 2 28)',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {addressApiError}
                                  </div>
                                ) : null}
                                {generateKeyError ? (
                                  <div
                                    style={{
                                      color: 'rgb(208 2 28)',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {generateKeyError}
                                  </div>
                                ) : null}
                              </div>
                            </SecurityContainer>
                          );
                        }
                        return null;
                      })()}
                      <Flex justify="space-between" width="140%">
                        <Button
                          variant="outline"
                          before={
                            <IconSymbol icon="arrow_back" color="$primary1" />
                          }
                          onClick={handleChooseAnotherMethod}
                          css={{
                            'abyss-button-root': {
                              marginLeft: '-70px',
                            },
                          }}
                        >
                          Choose another method
                        </Button>
                        <Button
                          type="submit"
                          after={
                            <IconSymbol
                              icon="arrow_forward"
                              color="$primary1"
                            />
                          }
                          css={{
                            'abyss-button-root': {
                              marginRight: '-35px',
                            },
                          }}
                        >
                          Next
                        </Button>
                      </Flex>
                    </USMailValidationView>
                  );
                },
                medicaidIdInfo: () => {
                  return (
                    <MedicaidId
                      handleChooseAnotherMethod={handleChooseAnotherMethod}
                      showMedicaidError={showMedicaidError}
                    />
                  );
                },
              };

              return displayObj[reduxMainRadio]();
            })()}
          </React.Fragment>
        )}
      </FormProvider>
    </React.Fragment>
  );
};

VerifyClaimV3.propTypes = {
  handleBack: PropTypes.func.isRequired,
  handleNext: PropTypes.func.isRequired,
  handleError: PropTypes.func.isRequired,
  handleMedicaidError: PropTypes.func.isRequired,
  showMedicaidError: PropTypes.string.isRequired,
  handleStopAll: PropTypes.func.isRequired,
  handleDisableCancel: PropTypes.func.isRequired,
  address: PropTypes.arrayOf(PropTypes.shape({})),
};

VerifyClaimV3.defaultProps = {
  address: [],
};
